import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import "./HomePage.css";
import { Combobox } from "../components/Combobox/Combobox.jsx";
import { ColumnChart } from "../components/ColumnChart/ColumnChart.jsx";
import { ThresholdMeterBar } from "../components/ThresholdMeter/ThresholdMeter.jsx";
import { useEscapeStack } from "../lib/shortcuts/useEscapeStack.js";
import {
  createHomeTileExpenseAction,
  runHomeTileExpenseAction
} from "../features/home/homeTileActions.js";
import {
  createHomeArgOptions,
  createHomeEditorOptions,
  createHomeTiles
} from "../features/home/homeTileValues.js";

function splitCurrencyText(text) {
  const normalizedText = String(text ?? "").replace(/[\u2066\u2069]/g, "");
  const match = normalizedText.match(/^([+-]?)([^\d.,\s]+)(.*)$/);

  if (!match) {
    return {
      symbol: "",
      amount: normalizedText
    };
  }

  return {
    symbol: `${match[1]}${match[2]}`,
    amount: match[3]
  };
}

function TileValue({ value }) {
  if (value?.kind !== "currency") {
    return <div class="home-tile-value">{value?.text ?? ""}</div>;
  }

  const currency = splitCurrencyText(value.text);

  return (
    <div class="home-tile-value" dir="ltr">
      <span class="home-tile-currency-symbol">{currency.symbol}</span>
      <span>{currency.amount}</span>
    </div>
  );
}

function TileChart({ chart }) {
  if (chart?.type !== "monthly-expenses") {
    return null;
  }

  return (
    <div class="home-tile-chart">
      <ColumnChart
        columns={chart.columns}
        minTrackHeight="2rem"
        showInlineLabels
        showLabels={false}
        showValueLabels={false}
      />
    </div>
  );
}

function TileMeter({ label, meter }) {
  if (!meter) {
    return null;
  }

  return (
    <div class="home-tile-meter">
      <ThresholdMeterBar
        label={label}
        value={meter.value}
        range={meter.range}
        startValue={meter.range.target}
      />
      <div class="home-tile-meter-value">{meter.valueLabel}</div>
    </div>
  );
}

const HOME_GRID_COLUMNS = 3;
const HOME_SELECTED_TILE_SESSION_KEY = "wallet-watch:home-selected-tile-index";

function readRememberedTileIndex(tileCount) {
  try {
    const storedValue = window.sessionStorage.getItem(HOME_SELECTED_TILE_SESSION_KEY);
    const index = Number(storedValue);

    return Number.isInteger(index) && index >= 0 && index < tileCount
      ? index
      : 0;
  } catch {
    return 0;
  }
}

function rememberTileIndex(index) {
  try {
    window.sessionStorage.setItem(HOME_SELECTED_TILE_SESSION_KEY, String(index));
  } catch {
    // ignore session storage failures
  }
}

function getNextTileIndex(index, key, tileCount, isRtl = false) {
  const previousKey = isRtl ? "ArrowRight" : "ArrowLeft";
  const nextKey = isRtl ? "ArrowLeft" : "ArrowRight";

  if (key === previousKey && index % HOME_GRID_COLUMNS > 0) {
    return index - 1;
  }

  if (key === nextKey && index % HOME_GRID_COLUMNS < HOME_GRID_COLUMNS - 1 && index + 1 < tileCount) {
    return index + 1;
  }

  if (key === "ArrowUp" && index - HOME_GRID_COLUMNS >= 0) {
    return index - HOME_GRID_COLUMNS;
  }

  if (key === "ArrowDown" && index + HOME_GRID_COLUMNS < tileCount) {
    return index + HOME_GRID_COLUMNS;
  }

  return index;
}

export function HomePage({
  activeLanguage,
  budgetPage,
  dateContext,
  navigateToPage,
  onExpensesVisibilityFilterChange,
  tileConfigs,
  onTileConfigsChange,
  isRtl = false,
  t = (key) => key
}) {
  const tileRefs = useRef([]);
  const editorOptionRefs = useRef([]);
  const argumentControlRef = useRef(null);
  const didInitialFocusRef = useRef(false);
  const [selectedTileKey, setSelectedTileKey] = useState(null);
  const [editingTileKey, setEditingTileKey] = useState(null);
  const [selectedEditorOptionIndex, setSelectedEditorOptionIndex] = useState(0);
  const [argumentEditorOption, setArgumentEditorOption] = useState(null);
  const [now, setNow] = useState(() => new Date());
  const tiles = useMemo(() => createHomeTiles(tileConfigs, {
    activeLanguage,
    rows: budgetPage.rows,
    expenseRows: budgetPage.expenseRows,
    dateContext,
    now,
    t
  }), [activeLanguage, budgetPage.expenseRows, budgetPage.rows, dateContext, now, t, tileConfigs]);
  const editorOptions = useMemo(() => createHomeEditorOptions(t), [t]);
  const tileActions = useMemo(() => (
    tileConfigs.map((config) => createHomeTileExpenseAction(config))
  ), [tileConfigs]);
  const argumentOptions = useMemo(() => (
    argumentEditorOption ? createHomeArgOptions(argumentEditorOption, budgetPage.expenseRows) : []
  ), [argumentEditorOption, budgetPage.expenseRows]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setNow(new Date());
    }, 1000);

    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (didInitialFocusRef.current || tiles.length === 0) {
      return;
    }

    didInitialFocusRef.current = true;
    const rememberedIndex = readRememberedTileIndex(tiles.length);
    setSelectedTileKey(tiles[rememberedIndex].key);
    focusTile(rememberedIndex);
  }, [tiles]);

  useEffect(() => {
    if (!editingTileKey) {
      return;
    }

    requestAnimationFrame(() => {
      editorOptionRefs.current[selectedEditorOptionIndex]?.scrollIntoView({
        block: "nearest"
      });
    });
  }, [editingTileKey, selectedEditorOptionIndex]);

  useEffect(() => {
    if (!argumentEditorOption) {
      return;
    }

    requestAnimationFrame(() => {
      argumentControlRef.current?.querySelector("input")?.focus();
    });
  }, [argumentEditorOption]);

  useEscapeStack(() => {
    if (!editingTileKey) {
      return false;
    }

    const editingTileIndex = tiles.findIndex((tile) => tile.key === editingTileKey);
    if (editingTileIndex < 0) {
      return false;
    }

    if (argumentEditorOption) {
      setArgumentEditorOption(null);
      focusTile(editingTileIndex);
      return true;
    }

    closeTileEditor(editingTileIndex);
    return true;
  }, Boolean(editingTileKey), 10);

  function focusTile(index) {
    requestAnimationFrame(() => {
      tileRefs.current[index]?.focus();
    });
  }

  function selectTile(index) {
    setSelectedTileKey(tiles[index].key);
    rememberTileIndex(index);
  }

  function moveTileSelection(index, key) {
    const nextIndex = getNextTileIndex(index, key, tiles.length, isRtl);

    if (nextIndex === index) {
      return;
    }

    selectTile(nextIndex);
    setEditingTileKey(null);
    setSelectedEditorOptionIndex(0);
    focusTile(nextIndex);
  }

  function openTileEditor(tileKey, tileIndex) {
    const currentMetricKey = tileConfigs[tileIndex]?.metricKey;
    const currentOptionIndex = editorOptions.findIndex((option) => option.key === currentMetricKey);

    selectTile(tileIndex);
    setEditingTileKey(tileKey);
    setSelectedEditorOptionIndex(currentOptionIndex >= 0 ? currentOptionIndex : 0);
    setArgumentEditorOption(null);
  }

  function closeTileEditor(index) {
    setEditingTileKey(null);
    setSelectedEditorOptionIndex(0);
    setArgumentEditorOption(null);
    focusTile(index);
  }

  function updateTileConfig(tileIndex, config) {
    const nextConfigs = tileConfigs.map((entry, index) => (
      index === tileIndex ? config : entry
    ));
    onTileConfigsChange?.(nextConfigs);
  }

  function applyMetricOption(option, tileIndex) {
    updateTileConfig(tileIndex, {
      metricKey: option.key,
      args: {}
    });
    setEditingTileKey(null);
    setArgumentEditorOption(null);
  }

  function applyArgumentValue(tileIndex, option, value) {
    if (!value) {
      return;
    }

    updateTileConfig(tileIndex, {
      metricKey: option.key,
      args: {
        [option.arg.key]: value
      }
    });
    setEditingTileKey(null);
    setArgumentEditorOption(null);
    focusTile(tileIndex);
  }

  function selectEditorOption(option, optionIndex, tileIndex) {
    setSelectedEditorOptionIndex(optionIndex);

    if (option.arg) {
      setArgumentEditorOption(option);
      return;
    }

    applyMetricOption(option, tileIndex);
  }

  function moveEditorOption(direction) {
    setSelectedEditorOptionIndex((current) => {
      const nextIndex = current + direction;

      if (nextIndex < 0 || nextIndex >= editorOptions.length) {
        return current;
      }

      return nextIndex;
    });
  }

  function selectEditorOptionIndex(optionIndex) {
    setSelectedEditorOptionIndex(Math.max(0, Math.min(optionIndex, editorOptions.length - 1)));
  }

  function handleTileKeyDown(event, tile, index) {
    if (editingTileKey === tile.key) {
      if (argumentEditorOption) {
        if (event.target !== event.currentTarget) {
          return;
        }

        if (event.key === " " || event.key === "Enter") {
          event.preventDefault();
          closeTileEditor(index);
        }

        return;
      }

      if (event.key === "ArrowUp") {
        event.preventDefault();
        moveEditorOption(-1);
        return;
      }

      if (event.key === "ArrowDown") {
        event.preventDefault();
        moveEditorOption(1);
        return;
      }

      if (event.key === "Home") {
        event.preventDefault();
        selectEditorOptionIndex(0);
        return;
      }

      if (event.key === "End") {
        event.preventDefault();
        selectEditorOptionIndex(editorOptions.length - 1);
        return;
      }

      if (event.key === " " || event.key === "Enter") {
        event.preventDefault();
        selectEditorOption(editorOptions[selectedEditorOptionIndex], selectedEditorOptionIndex, index);
      }

      return;
    }

    if (event.target !== event.currentTarget) {
      return;
    }

    if (event.key.startsWith("Arrow")) {
      event.preventDefault();
      moveTileSelection(index, event.key);
      return;
    }

    if (event.key === " ") {
      event.preventDefault();
      openTileEditor(tile.key, index);
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      runHomeTileExpenseAction(tileActions[index], {
        navigateToPage,
        onExpensesVisibilityFilterChange
      });
    }
  }

  return (
    <section class="home-page">
      <div class="home-grid">
        {tiles.map((tile, index) => (
          <article
            class={`home-tile${tile.isBlank ? " is-blank" : ""}${selectedTileKey === tile.key ? " is-selected" : ""}${editingTileKey === tile.key ? " is-editing" : ""}`}
            key={tile.key}
            onClick={() => {
              selectTile(index);
              focusTile(index);
            }}
            onDblClick={() => {
              openTileEditor(tile.key, index);
              focusTile(index);
            }}
            onFocus={() => selectTile(index)}
            onKeyDown={(event) => handleTileKeyDown(event, tile, index)}
            ref={(element) => {
              tileRefs.current[index] = element;
            }}
            tabIndex={0}
          >
            {editingTileKey === tile.key ? (
              <div class="home-tile-editor">
                {argumentEditorOption ? (
                  <div
                    class="home-tile-argument-editor"
                    onClick={(event) => event.stopPropagation()}
                    onDblClick={(event) => event.stopPropagation()}
                  >
                    <div class="home-tile-argument-label">
                      {argumentEditorOption.label}{" "}
                      <span class="home-tile-editor-argument">[{argumentEditorOption.argument}]</span>
                    </div>
                    <div
                      class="home-tile-argument-combobox"
                      ref={argumentControlRef}
                    >
                      <Combobox
                        className="home-tile-argument-control"
                        commitEmptyToFirst={false}
                        maxVisibleOptions={5}
                        onChange={(value) => applyArgumentValue(index, argumentEditorOption, value)}
                        options={argumentOptions}
                        placeholder={t("choose")}
                        value=""
                      />
                    </div>
                  </div>
                ) : (
                  <div class="home-tile-editor-list">
                    {editorOptions.map((option, optionIndex) => (
                      <div
                        class={`home-tile-editor-option${selectedEditorOptionIndex === optionIndex ? " is-selected" : ""}`}
                        key={option.key}
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedEditorOptionIndex(optionIndex);
                        }}
                        onDblClick={(event) => {
                          event.stopPropagation();
                          selectEditorOption(option, optionIndex, index);
                        }}
                        ref={(element) => {
                          editorOptionRefs.current[optionIndex] = element;
                        }}
                      >
                        <span class="home-tile-editor-option-number">{optionIndex + 1}</span>
                        <span class="home-tile-editor-option-label">
                          {option.label}
                          {option.argument ? (
                            <>
                              {" "}
                              <span class="home-tile-editor-argument">[{option.argument}]</span>
                            </>
                          ) : null}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <>
                <div class="home-tile-label">{tile.label}</div>
                {tile.meter ? (
                  <TileMeter label={tile.label} meter={tile.meter} />
                ) : tile.chart ? (
                  <TileChart chart={tile.chart} />
                ) : (
                  <TileValue value={tile.value} />
                )}
                {tileActions[index] && selectedTileKey === tile.key ? (
                  <span class="home-tile-action-indicator">i</span>
                ) : null}
              </>
            )}
          </article>
        ))}
      </div>
    </section>
  );
}
